import { useEffect, useState } from "react";
import axios from "axios";
import Select from "react-select";
import DataTable from "react-data-table-component";
import { ThreeDot } from "react-loading-indicators";
import BASE_URL from "../../../assets/global/url";
import NoAccess from "../../../assets/img/NoAccess.png";
import { selectCustomStyles } from "../../../assets/global/selectCustomStyles";
import { customStyles } from "../../../assets/global/table-style";
import useDecodeToken from "../../../hooks/customHook/useDecodeToken";
import {
  usePagination,
  PaginationControls,
} from "../../../hooks/customHook/paginationHook/usePagination";
import { getUserAccessType } from "../../../services/general/getUserAccessType.service";
import DateRangePicker from "../../../components/DateRangePicker";
import { NoTranslate } from "../../../helper/prevent_translate_browser";
import { getMonthBoundaries } from "../../../utils/newdate";
import {
  formatDateForAPI,
  formatDateForColumn,
} from "../../../utils/dateFormatter";
import { formatToTwoDecimal } from "../../../utils/numberFormatter";
import {
  exportUserCollectionReport,
  exportUserCollectionPDF,
} from "../../../utils/exportCSV/exportUserCollectionReport";
import "../../../assets/css/style.css";

// Standard Reports > User Reports (content to follow)

// Report choices, shown as pill tabs (same style as Account Reports)
const REPORT_TYPES = [
  { value: "sales", label: "Sales", icon: "fa-file-invoice-dollar" },
  { value: "collection", label: "Collection", icon: "fa-hand-holding-dollar" },
  { value: "issued_pdc", label: "Issued PDC", icon: "fa-money-check" },
];

// Not in fetchAllUsers; sees every user's data by default
const SUPERADMIN_ID = "11111111-1111-1111-1111-111111111111";

// Masterlist user -> react-select option ("First Middle Last")
const toOption = (user) => ({
  value: user.id,
  label: `${user.fname} ${user.mname ? user.mname + " " : ""}${user.lname}`,
});

// Light header instead of the app-wide purple one (same as Account Reports)
const tableStyles = {
  ...customStyles,
  headCells: {
    style: {
      ...customStyles.headCells.style,
      backgroundColor: "var(--sa-surface-2)",
      color: "var(--sa-muted)",
      fontWeight: "600",
      fontSize: "14px",
      padding: "14px 8px",
    },
  },
  progress: {
    style: {
      ...customStyles.progress?.style,
      backgroundColor: "var(--sa-surface)",
      color: "var(--sa-muted)",
    },
  },
  noData: {
    style: {
      ...customStyles.noData?.style,
      backgroundColor: "var(--sa-surface)",
      color: "var(--sa-muted)",
    },
  },
};

const amountCell = (amount) => (
  <NoTranslate>
    <span className="sa-amount fw-semibold">{formatToTwoDecimal(amount)}</span>
  </NoTranslate>
);

const salesColumns = [
  {
    name: "Date",
    selector: (row) => formatDateForColumn(row.date),
  },
  {
    name: "DR",
    // "---" from the backend when there's no DR number
    selector: (row) => <NoTranslate>{row.dr_number}</NoTranslate>,
  },
  {
    name: "Customer",
    selector: (row) => <NoTranslate>{row.customer_name}</NoTranslate>,
    grow: 2,
  },
  {
    name: "AMT",
    selector: (row) => amountCell(row.amount),
  },
];

const collectionColumns = [
  {
    name: "Date",
    selector: (row) => formatDateForColumn(row.date),
  },
  {
    name: "Payee Name / Customer",
    selector: (row) => <NoTranslate>{row.customer_name}</NoTranslate>,
    grow: 2,
  },
  {
    name: "Account Name",
    selector: (row) => <NoTranslate>{row.account_name}</NoTranslate>,
    grow: 2,
  },
  {
    name: "Check #",
    selector: (row) => <NoTranslate>{row.check_number}</NoTranslate>,
  },
  {
    name: "Check Date",
    selector: (row) =>
      row.check_date ? formatDateForColumn(row.check_date) : "",
  },
  {
    name: "Amount",
    selector: (row) => amountCell(row.amount),
  },
  {
    name: "Status",
    // "Approved" / "Collected" from the backend, blank while pending
    selector: (row) => row.status,
    width: "120px",
  },
];

// Per report tab: where its rows come from, its columns, and the values
// the search box matches (every visible column)
const REPORTS = {
  sales: {
    endpoint: "/userReport/getSalesReport",
    columns: salesColumns,
    noun: "sales",
    searchValues: (row) => [
      formatDateForColumn(row.date),
      row.dr_number,
      row.customer_name,
      formatToTwoDecimal(row.amount),
    ],
  },
  collection: {
    endpoint: "/userReport/getCollectionReport",
    columns: collectionColumns,
    noun: "collections",
    searchValues: (row) => [
      formatDateForColumn(row.date),
      row.customer_name,
      row.account_name,
      row.check_number,
      row.check_date ? formatDateForColumn(row.check_date) : "",
      formatToTwoDecimal(row.amount),
      row.status,
    ],
  },
};

const matchesSearch = (values, text) =>
  values.some((value) =>
    String(value || "")
      .toLowerCase()
      .includes(text),
  );

const UserReports = ({ authrztn = [] }) => {
  const userLoggedID = useDecodeToken();
  const [userList, setUserList] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [reportType, setReportType] = useState(REPORT_TYPES[0].value);
  // Same date range picker as Sales; starts on the current month
  const [dateRange, setDateRange] = useState(() => {
    const { start, end } = getMonthBoundaries();
    return { from: start, to: end };
  });
  // Access type of the logged in user's role: "Management" can pick any user,
  // "Employee" is locked to themselves
  const [accessType, setAccessType] = useState("");
  const isSuperadmin = userLoggedID === SUPERADMIN_ID;
  // Superadmin can also pick any user
  const isManagement = accessType === "Management" || isSuperadmin;
  // Rows of the current report tab
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const report = REPORTS[reportType];

  // useDecodeToken starts as [] until the token is decoded
  useEffect(() => {
    if (typeof userLoggedID !== "string") return;
    getUserAccessType(userLoggedID)
      .then(setAccessType)
      .catch((error) => console.error("Error fetching access type:", error));
  }, [userLoggedID]);

  // fetchAllUsers already leaves out the superadmin (11111111-...)
  useEffect(() => {
    axios
      .get(BASE_URL + "/masterList/fetchAllUsers")
      .then((response) => setUserList(response.data))
      .catch((error) => console.error("Error fetching users:", error));
  }, []);

  const userOptions = userList.map(toOption);
  // The logged in user's own option: the default for everyone, and what
  // clearing goes back to (Employee access is locked to it)
  const selfOption =
    userOptions.find((option) => option.value === userLoggedID) || null;

  useEffect(() => {
    const self = userList.find((user) => user.id === userLoggedID);
    if (self) setSelectedUser(toOption(self));
  }, [userList, userLoggedID]);

  const selectedUserId = selectedUser?.value;
  useEffect(() => {
    // Clear right away so one tab's rows never show under another's columns
    setRows([]);
    // No user selected only happens for the superadmin: load every user
    const endpoint = REPORTS[reportType]?.endpoint;
    if (!endpoint || (!selectedUserId && !isSuperadmin)) {
      setLoading(false);
      return;
    }

    // Ignore a slow response once the tab or filters have changed
    let stale = false;
    setLoading(true);
    axios
      .get(BASE_URL + endpoint, {
        params: {
          userId: selectedUserId || undefined,
          fromDate: formatDateForAPI(dateRange.from),
          toDate: formatDateForAPI(dateRange.to),
        },
      })
      .then((response) => !stale && setRows(response.data))
      .catch((error) =>
        console.error(`Error fetching ${reportType} report:`, error),
      )
      .finally(() => !stale && setLoading(false));
    return () => {
      stale = true;
    };
  }, [reportType, selectedUserId, isSuperadmin, dateRange]);

  const search = searchText.trim().toLowerCase();
  const filteredRows =
    report && search
      ? rows.filter((row) => matchesSearch(report.searchValues(row), search))
      : rows;
  const filteredCollectionRows =
    reportType === "collection" ? filteredRows : [];
  // Exports every filtered row, not just the current page
  const handleExportCollection = (type) => {
    const who = selectedUser?.label || "All Users";
    const from = formatDateForAPI(dateRange.from);
    const to = formatDateForAPI(dateRange.to);
    const fileName = `Collection Report - ${who} (${from} to ${to})`;
    if (type === "pdf") {
      exportUserCollectionPDF(
        filteredCollectionRows,
        fileName,
        `${who} | ${formatDateForColumn(dateRange.from)} - ${formatDateForColumn(dateRange.to)}`,
      );
      return;
    }
    // Excel also lists, in its own table, the customers with no
    // transaction in the exported rows
    const withTransaction = new Set(
      filteredCollectionRows.map((row) => row.customer_name),
    );
    axios
      .get(BASE_URL + "/userReport/getCustomerNames")
      .then((response) =>
        exportUserCollectionReport(
          filteredCollectionRows,
          fileName,
          response.data.filter((name) => !withTransaction.has(name)),
        ),
      )
      .catch((error) => console.error("Error exporting collection:", error));
  };
  const canExport =
    reportType === "collection" &&
    !loading &&
    filteredCollectionRows.length > 0;

  // Same pagination controls as Account Reports, paged on the client
  const pagination = usePagination(filteredRows, 10);

  // Back to page 1 whenever the rows change
  useEffect(() => {
    pagination.firstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, rows]);

  if (!authrztn.includes("UserReport-View")) {
    return (
      <div className="h-100 w-100 border bg-white custom-container">
        <div className="no-access">
          <img src={NoAccess} alt="NoAccess" className="no-access-img" />
          <h3>You don't have access to this function.</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="h-100 w-100 border custom-container sa-page">
      {/* Header */}
      <div className="sa-header">
        <div className="d-flex align-items-center gap-3">
          <div className="sa-header-icon">
            <i className="fa-solid fa-users"></i>
          </div>
          <div className="title-custom">
            <h1 className="sa-title">User Reports</h1>
          </div>
        </div>
        {/* Same Export dropdown as View Account */}
        {reportType === "collection" && (
          <div className="btn-group align-self-center">
            <button
              type="button"
              className="btn btn-success dropdown-toggle px-3"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              disabled={!canExport}
            >
              <i className="fa-solid fa-file-export me-2"></i>
              Export
            </button>
            <ul className="dropdown-menu dropdown-menu-end">
              <li>
                <button
                  type="button"
                  className="dropdown-item"
                  onClick={() => handleExportCollection("pdf")}
                >
                  <i className="fa-solid fa-file-pdf me-2 text-danger"></i>
                  PDF
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className="dropdown-item"
                  onClick={() => handleExportCollection("excel")}
                >
                  <i className="fa-solid fa-file-excel me-2 text-success"></i>
                  Excel
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>

      <div className="sa-card">
        <div className="ar-filters align-items-end mb-0">
          <div
            className="ar-filter-group"
            style={{ width: "400px", maxWidth: "100%" }}
          >
            <span className="ar-filter-label">User</span>
            <Select
              options={userOptions}
              value={selectedUser}
              onChange={(selected) => setSelectedUser(selected || selfOption)}
              placeholder={
                isSuperadmin
                  ? "All users"
                  : isManagement
                    ? "Search or select user..."
                    : "Management access only"
              }
              isDisabled={!isManagement}
              isSearchable
              noOptionsMessage={({ inputValue }) =>
                inputValue ? `No users match "${inputValue}"` : "No users found"
              }
              isClearable
              styles={selectCustomStyles(selectedUser, false, "0.3rem")}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          <DateRangePicker
            startDate={dateRange.from}
            endDate={dateRange.to}
            onDateRangeChange={(from, to) => setDateRange({ from, to })}
            label="Date Range"
          />

          {/* Report tabs pushed to the right of the filters */}
          <div className="ar-type-tabs ms-auto">
            {REPORT_TYPES.map((type) => (
              <button
                key={type.value}
                type="button"
                className={`ar-type-tab ${
                  reportType === type.value ? "active" : ""
                }`}
                onClick={() => setReportType(type.value)}
              >
                <i className={`fa-solid ${type.icon}`}></i>
                {type.label}
              </button>
            ))}
          </div>
        </div>

        <div className="sa-search mt-3">
          <i className="fa-solid fa-magnifying-glass"></i>
          <input
            type="text"
            className="form-control"
            placeholder="Search..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          {searchText && (
            <button
              type="button"
              className="sa-search-clear"
              title="Clear search"
              onClick={() => setSearchText("")}
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
          )}
        </div>

        {report && (
          <>
            <DataTable
              columns={report.columns}
              data={pagination.paginatedData}
              customStyles={tableStyles}
              className="dataTable"
              progressPending={loading}
              progressComponent={
                <div className="sa-table-state">
                  <ThreeDot
                    variant="brick-stack"
                    color="#6290FE"
                    size="small"
                    text="Fetching records..."
                    textColor="#6c757d"
                  />
                </div>
              }
              noDataComponent={
                <div className="sa-table-state">
                  <i className="fa-regular fa-folder-open"></i>
                  <span>
                    {searchText
                      ? `No ${report.noun} match "${searchText}"`
                      : `No ${report.noun} found`}
                  </span>
                </div>
              }
              highlightOnHover
            />
            <div className="sa-pagination">
              <PaginationControls {...pagination} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UserReports;
